import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    // Update password in state but don't expose it in HTML
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }
    
    if (form.newPassword !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    if (form.newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: token,
          newPassword: form.newPassword,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(true);
        setForm({ newPassword: '', confirmPassword: '' });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } else {
        setError(data.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-100">
        <div className="w-full max-w-md mx-auto">
          <div className="flex flex-col items-center mb-8">
            <span className="inline-block h-16 w-16 rounded-full shadow-lg mb-2 bg-green-100 flex items-center justify-center">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17l-5-5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <h1 className="text-3xl font-extrabold text-green-700 mb-1 tracking-tight">Password Reset Success!</h1>
            <p className="text-gray-500 text-center">Your password has been successfully reset. Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-100">
      <div className="w-full max-w-md mx-auto">
        <div className="flex flex-col items-center mb-8">
          <span className="inline-block h-16 w-16 rounded-full shadow-lg mb-2 bg-blue-100 flex items-center justify-center">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="24" height="24" rx="12" fill="#2563eb"/>
              <path d="M7 9l5 4 5-4" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="4" y="7" width="16" height="10" rx="2" stroke="#fff" strokeWidth="2"/>
            </svg>
          </span>
          <h1 className="text-3xl font-extrabold text-blue-700 mb-1 tracking-tight">Reset Password</h1>
          <p className="text-gray-500 text-center">Enter your new password below</p>
        </div>
        
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 dark:border-gray-800 rounded-2xl shadow-2xl p-8 space-y-6 border border-blue-100">
          {error && <div className="text-red-600 text-center mb-2 font-medium">{error}</div>}
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">New Password</label>
            <input
              name="newPassword"
              onChange={handlePasswordChange}
              required
              type="password"
              placeholder="Enter your new password"
              className="input w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
              autoFocus
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Confirm Password</label>
            <input
              name="confirmPassword"
              onChange={handlePasswordChange}
              required
              type="password"
              placeholder="Confirm your new password"
              className="input w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold text-lg shadow hover:bg-blue-700 transition disabled:opacity-50"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
          
          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-sm text-blue-600 hover:underline focus:outline-none"
            >
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 